"use client"

import { ChevronUp, ShoppingCartIcon, Trash2 } from "lucide-react"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import type {
    InitiateBankTransferResponse,
    QuotePaymentResponse,
} from "@/lib/types/payment.types"
import {
    formatPaymentMethodHint,
    mergePaymentMethodsConfig,
    refreshAppointmentPaymentMethods,
} from "@/lib/utils/payment-methods"
import { useEffect, useRef, useState } from "react"

import { ApiService } from "@/lib/api"
import { AuthUserButton } from "@/app/components/common/AuthUserButton"
import { Button } from "@/components/ui/button"
import LoginModal from "../screens/auth/LoginModal"
import { OnboardingModal } from "../screens/auth/OnboardingModal"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "../ui/skeleton"
import { format } from "date-fns"
import { getApiErrorMessage } from "@/lib/utils/api-error"
import { observer } from "mobx-react-lite"
import { openPaystackPopupFromInitiate } from "@/lib/payments/paystack-handler"
import { toastUtils } from "@/utils/toast-utils"
import { useStore } from "@/app/components/common/store.provider"
import { wallClockToIso } from "@/lib/utils/business-timezone"

function formatDuration(mins: number): string {
    if (mins < 60) return `${mins} mins`
    const h = Math.floor(mins / 60)
    const m = mins % 60
    if (m === 0) return `${h} hr${h > 1 ? "s" : ""}`
    return `${h} hr${h > 1 ? "s" : ""}, ${m} mins`
}

function toPriceLabel(price: number): string {
    return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 2,
    }).format(price)
}

function addMinutesToTime(time: string, minutes: number): string {
    const match = time.match(/^(\d{1,2}):(\d{2})/)
    if (!match) return time
    const h = parseInt(match[1], 10)
    const m = parseInt(match[2], 10)
    const totalMinutes = h * 60 + m + minutes
    const newH = Math.floor(totalMinutes / 60) % 24
    const newM = totalMinutes % 60
    return `${newH.toString().padStart(2, "0")}:${newM.toString().padStart(2, "0")}`
}

type EmptyCartFeedbackProps = {
    isAuthenticated: boolean
    isHydrating: boolean
    currentStep: number
    onSignIn?: () => void
}

const EmptyCartFeedback = ({
    isAuthenticated,
    isHydrating,
    currentStep,
    onSignIn,
}: EmptyCartFeedbackProps) => (
    <div className="flex flex-col items-center justify-center h-full">
        <div className="flex flex-col items-center justify-center text-center">
            <div className="flex items-center justify-center mb-6 rounded-full p-4 border">
                <ShoppingCartIcon className="h-10 w-10" />
            </div>
            {isHydrating ? (
                <>
                    <h2 className="text-xl font-bold">Loading your selection</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Restoring services from your booking link…
                    </p>
                    <div className="mt-4 w-full max-w-xs space-y-2">
                        <Skeleton className="h-16 w-full" />
                        <Skeleton className="h-16 w-full" />
                    </div>
                </>
            ) : (
                <>
                    <h2 className="text-2xl font-bold">No services selected</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        {currentStep > 1
                            ? "Go back to step 1 and choose at least one service to continue."
                            : "Select one or more services from the list above."}
                    </p>
                    {!isAuthenticated && currentStep >= 2 ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="default"
                            className="mt-4 rounded-full py-5"
                            onClick={onSignIn}
                        >
                            Sign in to continue
                        </Button>
                    ) : null}
                </>
            )}
        </div>
    </div>
)

type AppointmentCartProps = {
    onCreated?: () => void
}

export const AppointmentCart = observer(function AppointmentCart({ onCreated }: AppointmentCartProps) {
    const { appointmentStore, authStore } = useStore()
    const [loginOpen, setLoginOpen] = useState(false)
    const [onboardingOpen, setOnboardingOpen] = useState(false)
    const [mobileExpanded, setMobileExpanded] = useState(false)
    const [paymentError, setPaymentError] = useState<{ message: string } | null>(null)
    const [promptIntent, setPromptIntent] = useState<"promote_custom" | "confirm_custom_submit" | null>(null)
    const [transferDetails, setTransferDetails] = useState<{
        mode: "manual" | "paystack_dynamic"
        bankName?: string
        accountNumber?: string
        accountName?: string
        amount?: number
        authorizationUrl?: string
        paymentReference?: string
    } | null>(null)
    const [priceQuote, setPriceQuote] = useState<QuotePaymentResponse | null>(null)
    const [quoteLoading, setQuoteLoading] = useState(false)
    const appointmentIdForCardRetryRef = useRef<number | null>(null)
    const services = appointmentStore.selectedServices
    const isAuthenticated = authStore.isAuthenticated

    useEffect(() => {
        if (!loginOpen || !isAuthenticated) return
        void authStore.refreshMeFromGraphQL()
    }, [authStore, isAuthenticated, loginOpen])

    useEffect(() => {
        if (appointmentStore.paymentMethodsConfig) return
        void refreshAppointmentPaymentMethods(appointmentStore).catch(() => { })
    }, [appointmentStore])

    const paymentMethodsConfig = mergePaymentMethodsConfig(
        appointmentStore.paymentMethodsConfig ?? undefined,
    )
    const cashDepositPct = paymentMethodsConfig.cash.depositPercentage ?? 0
    const paymentMethod = appointmentStore.selectedPaymentMethod
    const quotePurpose: "full" | "deposit" =
        paymentMethod === "Cash" && cashDepositPct > 0 ? "deposit" : "full"

    const serviceIdsKey = services.map((s) => s.id).join(",")

    useEffect(() => {
        if (appointmentStore.currentStep !== 3 || services.length === 0) {
            setPriceQuote(null)
            return
        }
        let mounted = true
        setQuoteLoading(true)
        void ApiService.quotePayment({
            serviceIds: services.map((s) => s.id),
            discountCode: appointmentStore.discountCode?.trim() || undefined,
            paymentPurpose: quotePurpose,
        })
            .then((quote) => {
                if (mounted) setPriceQuote(quote)
            })
            .catch(() => {
                if (mounted) setPriceQuote(null)
            })
            .finally(() => {
                if (mounted) setQuoteLoading(false)
            })
        return () => {
            mounted = false
        }
    }, [
        appointmentStore.currentStep,
        appointmentStore.discountCode,
        quotePurpose,
        serviceIdsKey,
    ])

    // Clear retry state when date/slot changes (new appointment would be needed)
    useEffect(() => {
        appointmentIdForCardRetryRef.current = null
        setPaymentError(null)
    }, [appointmentStore.selectedDate, appointmentStore.selectedSlot])

    const profile = authStore.user?.profile
    const needsOnboarding =
        !profile ||
        !profile.firstName?.trim() ||
        !profile.lastName?.trim() ||
        !profile.phone?.trim()

    const handleLoginSuccess = () => {
        setLoginOpen(false)
        const p = authStore.user?.profile
        const needsProfile =
            !p || !p.firstName?.trim() || !p.lastName?.trim() || !p.phone?.trim()
        if (needsProfile) setOnboardingOpen(true)
        else appointmentStore.nextStep()
    }

    if (services.length === 0) {
        return (
            <div className="flex h-full flex-col">
                <div className="hidden flex-1 p-3 md:p-4 lg:block">
                    <EmptyCartFeedback
                        isAuthenticated={isAuthenticated}
                        isHydrating={appointmentStore.isBookingHydrating}
                        currentStep={appointmentStore.currentStep}
                        onSignIn={() => setLoginOpen(true)}
                    />
                </div>
                <div className="shrink-0 space-y-2 border-t border-border bg-background p-3 md:p-4">
                    <p className="text-sm text-muted-foreground lg:hidden">
                        {appointmentStore.isBookingHydrating
                            ? "Loading your selection…"
                            : "No services selected yet"}
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-muted-foreground">Total: —</p>
                        <Button
                            variant="default"
                            size="default"
                            className="w-full rounded-full py-5 sm:w-auto sm:min-w-36"
                            disabled
                        >
                            Continue
                        </Button>
                    </div>
                    <AuthUserButton
                        color="brand"
                        showLabel="always"
                        showSignInAction
                        onClick={() => {
                            if (!isAuthenticated) setLoginOpen(true)
                        }}
                    />
                </div>
                <LoginModal
                    open={loginOpen}
                    onOpenChange={setLoginOpen}
                    onSuccess={handleLoginSuccess}
                    shouldCloseOnSuccess={true}
                    hasTrigger={false}
                />
            </div>
        )
    }

    const total = services.reduce((sum, s) => sum + (Number(s.price) || 0), 0)
    const totalDuration = services.reduce((sum, s) => sum + (Number(s.duration) || 0), 0)
    const currentStep = appointmentStore.currentStep
    const isLoading = appointmentStore.isLoading
    const isCustomBooking = appointmentStore.isCustomBooking
    const serviceIds = services.map((s) => Number(s.id)).filter((id) => Number.isFinite(id) && id > 0)

    const hasAnyAvailabilityForCurrentSelection = async (): Promise<boolean> => {
        const range = await ApiService.getAvailableSlotsRange(
            appointmentStore.maxAvailableSlotsDays,
            {
                serviceIds,
                durationMinutes: totalDuration > 0 ? totalDuration : 60,
            },
        )
        return (range.days ?? []).some((day) => day.hasAvailability)
    }

    const closePromptModal = () => setPromptIntent(null)

    const openPromoteCustomModal = () => setPromptIntent("promote_custom")

    const openConfirmCustomSubmitModal = () => setPromptIntent("confirm_custom_submit")

    const handlePromptConfirm = () => {
        if (promptIntent === "promote_custom") {
            appointmentStore.setIsCustomBooking(true)
            closePromptModal()
            return
        }

        if (promptIntent === "confirm_custom_submit") {
            closePromptModal()
            void executeBookingSubmission()
        }
    }

    const handleContinue = () => {
        if (currentStep === 1 && services.length > 0) {
            if (isCustomBooking) {
                appointmentStore.nextStep()
                return
            }
            void (async () => {
                appointmentStore.setIsLoading(true)
                try {
                    const hasAvailability = await hasAnyAvailabilityForCurrentSelection()
                    if (!hasAvailability) {
                        openPromoteCustomModal()
                        return
                    }
                    appointmentStore.nextStep()
                } catch (error) {
                    const message = getApiErrorMessage(
                        error,
                        "Could not validate availability.",
                    )
                    toastUtils.error("Availability Check Failed", message)
                } finally {
                    appointmentStore.setIsLoading(false)
                }
            })()
        } else if (currentStep === 2 && !!appointmentStore.selectedDate && !!appointmentStore.selectedSlot) {
            if (!isAuthenticated) {
                setLoginOpen(true)
            } else if (isAuthenticated && needsOnboarding) {
                setOnboardingOpen(true)
            } else {
                appointmentStore.nextStep()
            }
        } else if (currentStep === 2 && isCustomBooking) {
            if (!isAuthenticated) {
                setLoginOpen(true)
            } else if (isAuthenticated && needsOnboarding) {
                setOnboardingOpen(true)
            } else {
                appointmentStore.nextStep()
            }
        } else if (currentStep === 3) {
            void handleConfirm()
        } else {
            appointmentStore.nextStep()
        }
    }

    const depositAmount =
        priceQuote?.depositAmount ??
        (cashDepositPct > 0 ? Math.round((total * cashDepositPct) / 100) : 0)
    const remainingAtVenue =
        priceQuote?.remainingAtVenue ?? total - depositAmount
    const displayTotal = priceQuote?.finalAmount ?? total
    const transferMethodHint =
        currentStep === 3 && appointmentStore.selectedPaymentMethod === "Transfer"
            ? formatPaymentMethodHint(paymentMethodsConfig, "bankTransfer", displayTotal)
            : undefined
    const manualTransferDetails =
        currentStep === 3 &&
        appointmentStore.selectedPaymentMethod === "Transfer" &&
        paymentMethodsConfig.bankTransfer.mode === "manual"
            ? paymentMethodsConfig.bankTransfer.manual
            : null
    const showManualTransferAccount =
        Boolean(
            manualTransferDetails?.bankName?.trim() &&
                manualTransferDetails?.accountNumber?.trim(),
        )

    const runCardPaymentFlow = async (
        appointmentId: number,
        paymentPurpose: "full" | "deposit" = "full",
    ) => {
        const email = authStore.user?.email
        if (!email) {
            toastUtils.error("Payment Failed", "Email is required for card payment.")
            return
        }
        const data = await ApiService.initiateCardPayment({
            customerEmail: email,
            appointmentId,
            discountCode: appointmentStore.discountCode?.trim() || undefined,
            paymentPurpose,
        })
        if (data.walletOnly) {
            toastUtils.success("Appointment Booked", "Your appointment was paid with wallet balance.")
            appointmentIdForCardRetryRef.current = null
            setPaymentError(null)
            appointmentStore.reset()
            onCreated?.()
            return
        }

        const paymentSettings = await ApiService.getPublicPaymentSettings()
        const publicKey =
            paymentSettings.paystackPublicKey?.trim() ||
            process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY?.trim()
        if (!publicKey) {
            toastUtils.error("Payment Failed", "Paystack is not configured.")
            return
        }
        if (!data.authorizationUrl && !data.accessCode) {
            throw new Error("Payment session is incomplete. Please try again.")
        }
        try {
            const paystackResult = await openPaystackPopupFromInitiate(
                publicKey,
                data,
                email,
            )
            const referenceToVerify = paystackResult.reference || data.paymentReference
            const verifyResult = await ApiService.verifyPayment(referenceToVerify)
            const status =
                verifyResult.data?.status ?? verifyResult.status
            if (status === "success" || status === "successful") {
                toastUtils.success(
                    "Appointment Booked",
                    "Your appointment and payment have been confirmed.",
                )
            } else {
                toastUtils.success(
                    "Appointment Booked",
                    "Your appointment has been created. Payment may still be processing.",
                )
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : ""
            if (message.toLowerCase().includes("redirecting to paystack")) {
                return
            }
            throw err
        }
        appointmentIdForCardRetryRef.current = null
        setPaymentError(null)
        appointmentStore.reset()
        onCreated?.()
    }

    const runTransferPaymentFlow = async (appointmentId: number) => {
        const email = authStore.user?.email
        const transferConfig = paymentMethodsConfig.bankTransfer

        if (transferConfig.mode === "manual") {
            const manual = transferConfig.manual
            setTransferDetails({
                mode: "manual",
                bankName: manual?.bankName,
                accountNumber: manual?.accountNumber,
                accountName: manual?.accountName,
                amount: displayTotal,
            })
            toastUtils.success(
                "Appointment Reserved",
                "Transfer the amount using the bank details shown below. Your booking stays pending until payment is confirmed.",
            )
            return
        }

        if (!email) {
            toastUtils.error("Payment Failed", "Email is required for bank transfer.")
            return
        }

        const data: InitiateBankTransferResponse = await ApiService.initiateBankTransfer({
            customerEmail: email,
            appointmentId,
        })

        setTransferDetails({
            mode: "paystack_dynamic",
            bankName: data.bankName,
            accountNumber: data.accountNumber,
            accountName: data.accountName,
            amount: data.amountToPay,
            authorizationUrl: data.authorizationUrl,
            paymentReference: data.paymentReference,
        })
        toastUtils.success(
            "Appointment Reserved",
            "Transfer the amount to the account below to complete payment. Your booking stays pending until payment is confirmed.",
        )
    }

    const executeBookingSubmission = async () => {
        const userId = authStore.user?.id
        if (typeof userId !== "number") {
            toastUtils.error("Error", "Please log in to confirm.")
            return
        }
        const date = appointmentStore.selectedDate
        const slot = appointmentStore.selectedSlot
        if (!isCustomBooking && (!date || !slot)) {
            toastUtils.error("Error", "Please select date and time.")
            return
        }
        let startTime: string | undefined
        let endTime: string | undefined
        let appointment_date: string | undefined
        let appointment_time: string | undefined
        if (!isCustomBooking && date && slot) {
            const tz = appointmentStore.businessTimezone
            const endTimeStr = addMinutesToTime(slot.startTime, totalDuration)
            startTime = wallClockToIso(date, slot.startTime, tz)
            endTime = wallClockToIso(date, endTimeStr, tz)
            appointment_date = date
            appointment_time = slot.startTime
            if (!startTime || !endTime) {
                toastUtils.error("Error", "Invalid date or time.")
                return
            }
        }
        const paymentMethod = appointmentStore.selectedPaymentMethod

        setPaymentError(null)
        appointmentStore.setIsLoading(true)
        try {
            let appointmentId: number

            const needsOnlinePayment =
                paymentMethod === "Card" ||
                (paymentMethod === "Cash" && cashDepositPct > 0) ||
                (paymentMethod === "Transfer" &&
                    paymentMethodsConfig.bankTransfer.mode === "paystack_dynamic")

            if (appointmentIdForCardRetryRef.current && needsOnlinePayment) {
                appointmentId = appointmentIdForCardRetryRef.current
            } else {
                const appointment = await ApiService.createAppointment({
                    userId,
                    serviceIds,
                    serviceId: serviceIds[0],
                    appointment_date,
                    appointment_time,
                    startTime,
                    endTime,
                    paymentMethod: isCustomBooking ? "Cash" : paymentMethod,
                    isPaymentComplete: false,
                    isCustomBooking,
                    discountCode: appointmentStore.discountCode?.trim() || undefined,
                    notes: appointmentStore.bookingNotes.trim() || undefined,
                    acceptTerms: true,
                    notificationsConsent: appointmentStore.notificationsConsent,
                })
                appointmentId = appointment.id
                if (needsOnlinePayment && !isCustomBooking) {
                    appointmentIdForCardRetryRef.current = appointment.id
                }
            }

            if (paymentMethod === "Card" && !isCustomBooking) {
                await runCardPaymentFlow(appointmentId, "full")
            } else if (paymentMethod === "Cash" && cashDepositPct > 0 && !isCustomBooking) {
                await runCardPaymentFlow(appointmentId, "deposit")
            } else if (paymentMethod === "Transfer" && !isCustomBooking) {
                await runTransferPaymentFlow(appointmentId)
                appointmentIdForCardRetryRef.current = null
            } else {
                appointmentIdForCardRetryRef.current = null
                if (isCustomBooking) {
                    toastUtils.success(
                        "Custom Booking Submitted",
                        "Your request has been submitted and is awaiting admin approval."
                    )
                } else {
                    toastUtils.success("Appointment Booked", "Your appointment has been confirmed.")
                }
                appointmentStore.reset()
                onCreated?.()
            }
        } catch (err) {
            const msg = getApiErrorMessage(err, "Could not complete booking.")
            const isPaymentCancel = msg.toLowerCase().includes("cancelled")
            const isRetryable =
                (paymentMethod === "Card" ||
                    (paymentMethod === "Cash" && cashDepositPct > 0)) &&
                !!appointmentIdForCardRetryRef.current

            if (isRetryable) {
                setPaymentError({
                    message: isPaymentCancel
                        ? "Payment was cancelled. Your appointment is pending until payment succeeds — retry below."
                        : msg,
                })
            } else {
                toastUtils.error(
                    isPaymentCancel ? "Payment cancelled" : "Booking Failed",
                    isPaymentCancel
                        ? "Your appointment is reserved as pending. Complete payment anytime from your appointments, or an admin can confirm it."
                        : msg
                )
                if (isPaymentCancel) onCreated?.()
            }
        } finally {
            appointmentStore.setIsLoading(false)
        }
    }

    const handleConfirm = () => {
        if (isCustomBooking) {
            openConfirmCustomSubmitModal()
            return
        }
        void executeBookingSubmission()
    }

    const handleRetryPayment = () => {
        setPaymentError(null)
        handleConfirm()
    }


    return (
        <div className="flex h-full flex-col">
            <div
                className={`min-h-0 flex-col space-y-3 overflow-y-auto scroll-smooth scrollbar-thin p-3 md:p-4 ${mobileExpanded ? "flex max-h-[40vh]" : "hidden"
                    } lg:flex lg:max-h-none lg:flex-1`}
            >
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground">Your selection</h3>
                    <button
                        type="button"
                        className="inline-flex min-h-11 items-center gap-1 px-2 text-sm text-muted-foreground lg:hidden"
                        onClick={() => setMobileExpanded(false)}
                    >
                        Hide
                        <ChevronUp className="h-4 w-4" />
                    </button>
                </div>
                {appointmentStore.selectedDate && appointmentStore.selectedSlot && (
                    <div className="rounded-lg border p-3 text-sm bg-muted/50">
                        <p className="font-medium text-foreground">Date & time</p>
                        <p className="mt-1 text-muted-foreground">
                            {format(new Date(`${appointmentStore.selectedDate}T12:00:00`), "EEEE, MMM d")} at {appointmentStore.selectedSlot.startTime}
                        </p>
                    </div>
                )}
                {services.map((s) => (
                    <div key={s.id} className="rounded-lg border p-3 text-sm">
                        <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-foreground">{s.name}</p>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                                aria-label={`Remove ${s.name}`}
                                onClick={() => appointmentStore.removeService(s.id)}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                        {s.description ? (
                            <div
                                className="mt-1 line-clamp-2 text-muted-foreground [&_a]:text-primary [&_a]:underline [&_p]:mb-0 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-4 [&_ol]:pl-4 [&_strong]:font-semibold"
                                dangerouslySetInnerHTML={{ __html: s.description }}
                            />
                        ) : null}
                        <div className="flex items-center gap-2">
                            <p className="mt-2 text-muted-foreground">{formatDuration(s.duration)}</p>
                            <Separator orientation="vertical" className="h-1 w-1 bg-secondary rounded-full mt-2" />
                            <p className="mt-2 text-muted-foreground">{s.targetAudience}</p>
                        </div>
                        <p className="font-semibold text-foreground">{toPriceLabel(s.price)}</p>
                    </div>
                ))}
                {isCustomBooking ? (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-100">
                        Custom booking is enabled. We will notify you after admin approval with your final date and time.
                    </div>
                ) : null}
                {currentStep === 3 &&
                    appointmentStore.selectedPaymentMethod === "Cash" &&
                    cashDepositPct > 0 ? (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                        <p className="font-medium text-foreground">Pay at venue deposit</p>
                        <p className="mt-1 text-muted-foreground">
                            Pay {cashDepositPct}% now ({toPriceLabel(depositAmount)}) to secure your booking.
                            {remainingAtVenue > 0
                                ? ` ${toPriceLabel(remainingAtVenue)} is due at the venue.`
                                : ""}
                        </p>
                    </div>
                ) : null}
                {showManualTransferAccount && manualTransferDetails ? (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm space-y-2">
                        <p className="font-medium text-foreground">
                            {paymentMethodsConfig.bankTransfer.label}
                        </p>
                        {paymentMethodsConfig.bankTransfer.description?.trim() ? (
                            <p className="text-muted-foreground">
                                {paymentMethodsConfig.bankTransfer.description}
                            </p>
                        ) : null}
                        <div className="space-y-2 rounded-md border bg-background/70 p-3">
                            <p>
                                <span className="text-muted-foreground">Bank: </span>
                                <span className="font-medium text-foreground">
                                    {manualTransferDetails.bankName}
                                </span>
                            </p>
                            <button
                                type="button"
                                className="flex w-full items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-left"
                                onClick={() => {
                                    void navigator.clipboard.writeText(
                                        manualTransferDetails.accountNumber ?? "",
                                    )
                                    toastUtils.success("Copied", "Account number copied.")
                                }}
                            >
                                <span>
                                    <span className="text-muted-foreground">Account number</span>
                                    <strong className="mt-0.5 block text-foreground">
                                        {manualTransferDetails.accountNumber}
                                    </strong>
                                </span>
                                <span className="text-xs text-primary">Copy</span>
                            </button>
                            {manualTransferDetails.accountName?.trim() ? (
                                <p>
                                    <span className="text-muted-foreground">Account name: </span>
                                    <span className="font-medium text-foreground">
                                        {manualTransferDetails.accountName}
                                    </span>
                                </p>
                            ) : null}
                            <p className="text-xs text-muted-foreground">
                                Transfer {toPriceLabel(displayTotal)} using these details. Your booking
                                stays pending until payment is confirmed.
                            </p>
                        </div>
                    </div>
                ) : transferMethodHint ? (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                        <p className="font-medium text-foreground">
                            {paymentMethodsConfig.bankTransfer.label}
                        </p>
                        <p className="mt-1 whitespace-pre-line text-muted-foreground">
                            {transferMethodHint}
                        </p>
                    </div>
                ) : null}
                {currentStep === 3 && !isCustomBooking ? (
                    <div className="rounded-lg border p-3 text-sm space-y-2">
                        <p className="font-medium text-foreground">Payment summary</p>
                        {quoteLoading ? (
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-3/4" />
                                <Skeleton className="h-4 w-1/2" />
                            </div>
                        ) : priceQuote ? (
                            <>
                                <div className="flex justify-between text-muted-foreground">
                                    <span>Services</span>
                                    <span>{toPriceLabel(priceQuote.appointmentTotal)}</span>
                                </div>
                                {priceQuote.discountAmount > 0 ? (
                                    <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-medium">
                                        <span>
                                            Discount
                                            {priceQuote.discountLabel ? ` (${priceQuote.discountLabel})` : ""}
                                        </span>
                                        <span>-{toPriceLabel(priceQuote.discountAmount)}</span>
                                    </div>
                                ) : null}
                                {(priceQuote.surchargeAmount ?? 0) > 0 ? (
                                    <div className="flex justify-between text-amber-600 dark:text-amber-400 font-medium">
                                        <span>Surcharge</span>
                                        <span>+{toPriceLabel(priceQuote.surchargeAmount ?? 0)}</span>
                                    </div>
                                ) : null}
                                {priceQuote.vatAmount > 0 ? (
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>VAT ({priceQuote.vatRate}%)</span>
                                        <span>{toPriceLabel(priceQuote.vatAmount)}</span>
                                    </div>
                                ) : null}
                                {priceQuote.platformFeeAmount > 0 ? (
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>
                                            Platform fee
                                            {priceQuote.platformFeeRate > 0
                                                ? ` (${priceQuote.platformFeeRate}%)`
                                                : ""}
                                        </span>
                                        <span>{toPriceLabel(priceQuote.platformFeeAmount)}</span>
                                    </div>
                                ) : null}
                                {priceQuote.walletAmountUsed > 0 ? (
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>Wallet</span>
                                        <span>-{toPriceLabel(priceQuote.walletAmountUsed)}</span>
                                    </div>
                                ) : null}
                                {quotePurpose === "deposit" &&
                                    (priceQuote.depositAmount ?? 0) > 0 ? (
                                    <>
                                        <Separator />
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Deposit due now</span>
                                            <span>{toPriceLabel(priceQuote.finalAmount)}</span>
                                        </div>
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Due at venue</span>
                                            <span>
                                                {toPriceLabel(priceQuote.remainingAtVenue ?? 0)}
                                            </span>
                                        </div>
                                    </>
                                ) : null}
                                <Separator />
                                <div className="flex justify-between font-semibold text-foreground">
                                    <span>
                                        {quotePurpose === "deposit" ? "Pay now" : "Total due"}
                                    </span>
                                    <span>{toPriceLabel(priceQuote.finalAmount)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="flex justify-between font-semibold text-foreground">
                                <span>Total</span>
                                <span>{toPriceLabel(total)}</span>
                            </div>
                        )}
                    </div>
                ) : null}
                {paymentError && (
                    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm">
                        <p className="text-destructive font-medium">{paymentError.message}</p>
                        <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 border-destructive/50 text-destructive hover:bg-destructive/10"
                            onClick={handleRetryPayment}
                            disabled={isLoading}
                        >
                            Retry payment
                        </Button>
                    </div>
                )}
            </div>
            <div className="shrink-0 space-y-2 border-t border-border bg-background p-3 md:p-4">
                <button
                    type="button"
                    className="flex min-h-11 w-full items-center justify-between text-left text-sm lg:hidden"
                    onClick={() => setMobileExpanded((open) => !open)}
                >
                    <span className="font-medium text-foreground">
                        {services.length} service{services.length === 1 ? "" : "s"} selected
                    </span>
                    <ChevronUp
                        className={`h-4 w-4 text-muted-foreground transition-transform ${mobileExpanded ? "" : "rotate-180"
                            }`}
                    />
                </button>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted-foreground">
                        {currentStep === 3 && priceQuote
                            ? `${quotePurpose === "deposit" ? "Pay now" : "Total"}: ${toPriceLabel(displayTotal)}`
                            : `Total: ${formatDuration(totalDuration)} · ${toPriceLabel(total)}`}
                    </p>
                    <Button
                        variant="default"
                        size="default"
                        className="w-full rounded-full py-5 sm:w-auto sm:min-w-36"
                        onClick={() => handleContinue()}
                        disabled={
                            isLoading ||
                            (currentStep === 1
                                ? services.length === 0
                                : currentStep === 2
                                    ? (!appointmentStore.selectedDate || !appointmentStore.selectedSlot) && !isCustomBooking
                                    : currentStep === 3
                                        ? !appointmentStore.acceptedTerms
                                        : false)
                        }
                    >
                        {isLoading
                            ? currentStep === 1
                                ? "Checking…"
                                : currentStep === 3
                                    ? "Confirming…"
                                    : "Loading…"
                            : currentStep === 3
                                ? "Confirm"
                                : "Continue"}
                    </Button>
                </div>
                <AuthUserButton
                    color="brand"
                    showLabel="always"
                    showSignInAction
                    onClick={() => {
                        if (!isAuthenticated) setLoginOpen(true)
                    }}
                />
            </div>
            <LoginModal
                open={loginOpen}
                onOpenChange={setLoginOpen}
                onSuccess={handleLoginSuccess}
                shouldCloseOnSuccess={true}
                hasTrigger={false}
            />
            <OnboardingModal
                open={onboardingOpen}
                onOpenChange={setOnboardingOpen}
                onSuccess={() => {
                    setOnboardingOpen(false)
                    appointmentStore.nextStep()
                }}
            />
            <Dialog
                open={transferDetails !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setTransferDetails(null)
                        appointmentStore.reset()
                        onCreated?.()
                    }
                }}
            >
                <DialogContent className="max-h-[90dvh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Complete your transfer</DialogTitle>
                        <DialogDescription>
                            {transferDetails?.mode === "manual"
                                ? "Transfer the full amount to the account below. Your booking is reserved."
                                : "Transfer the amount below within the time limit. Payment is confirmed automatically."}
                        </DialogDescription>
                    </DialogHeader>
                    {transferDetails ? (
                        <div className="space-y-3 text-sm">
                            {transferDetails.amount != null ? (
                                <button
                                    type="button"
                                    className="flex w-full items-center justify-between rounded-lg border bg-muted/40 px-3 py-3 text-left"
                                    onClick={() => {
                                        void navigator.clipboard.writeText(String(transferDetails.amount))
                                        toastUtils.success("Copied", "Amount copied to clipboard.")
                                    }}
                                >
                                    <span>
                                        <span className="text-muted-foreground">Amount</span>
                                        <strong className="mt-0.5 block">{toPriceLabel(transferDetails.amount)}</strong>
                                    </span>
                                    <span className="text-xs text-primary">Copy</span>
                                </button>
                            ) : null}
                            {transferDetails.bankName ? (
                                <p>
                                    <span className="text-muted-foreground">Bank: </span>
                                    {transferDetails.bankName}
                                </p>
                            ) : null}
                            {transferDetails.accountNumber ? (
                                <button
                                    type="button"
                                    className="flex w-full items-center justify-between rounded-lg border bg-muted/40 px-3 py-3 text-left"
                                    onClick={() => {
                                        void navigator.clipboard.writeText(transferDetails.accountNumber ?? "")
                                        toastUtils.success("Copied", "Account number copied.")
                                    }}
                                >
                                    <span>
                                        <span className="text-muted-foreground">Account</span>
                                        <strong className="mt-0.5 block">{transferDetails.accountNumber}</strong>
                                    </span>
                                    <span className="text-xs text-primary">Copy</span>
                                </button>
                            ) : null}
                            {transferDetails.accountName ? (
                                <p>
                                    <span className="text-muted-foreground">Name: </span>
                                    {transferDetails.accountName}
                                </p>
                            ) : null}
                            {transferDetails.authorizationUrl ? (
                                <Button asChild className="mt-2 w-full">
                                    <a
                                        href={transferDetails.authorizationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Open Paystack transfer page
                                    </a>
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                    <DialogFooter className="flex-col gap-2 sm:flex-row">
                        <Button
                            className="w-full sm:w-auto"
                            onClick={() => {
                                setTransferDetails(null)
                                appointmentStore.reset()
                                onCreated?.()
                            }}
                        >
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={promptIntent !== null} onOpenChange={(open) => (!open ? closePromptModal() : undefined)}>
                <DialogContent className="max-h-[90dvh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {promptIntent === "promote_custom"
                                ? "Switch to Custom Booking?"
                                : "Submit Custom Booking Request?"}
                        </DialogTitle>
                        <DialogDescription>
                            {promptIntent === "promote_custom"
                                ? "Your selected services may exceed available booking windows. You can switch to custom booking so the salon can review and approve a suitable time for you."
                                : "This booking will be submitted for manual approval. The final date, time, and amount confirmation will be sent after review."}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
                        <Button variant="outline" className="w-full sm:w-auto" onClick={closePromptModal}>
                            Cancel
                        </Button>
                        <Button className="w-full sm:w-auto" onClick={handlePromptConfirm}>
                            {promptIntent === "promote_custom" ? "Switch to custom" : "Submit request"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
})
